import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ServiceListOption {
  value: string;
  label: string;
  description: string;
  taxRate: number | null;
}

interface ServiceListSearchResponse {
  options: ServiceListOption[];
  totalCount: number;
}

const DEBOUNCE_MS = 300;

function useDebouncedValue<T>(value: T, delay = DEBOUNCE_MS): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

async function fetchServiceList(
  municipalityCode: string,
  query: string,
): Promise<ServiceListSearchResponse> {
  const { data, error } = await supabase.functions.invoke<ServiceListSearchResponse>("fiscal-reference-data", {
    body: {
      type: "service_list",
      municipalityCode,
      q: query,
    },
  });

  if (error) throw error;

  return data ?? { options: [], totalCount: 0 };
}

export function useFocusServiceListSearch({
  municipalityCode,
  query,
  enabled = true,
}: {
  municipalityCode: string;
  query: string;
  enabled?: boolean;
}) {
  const debouncedQuery = useDebouncedValue(query);
  const shouldSearch = enabled && !!municipalityCode && debouncedQuery.trim().length >= 2;

  return useQuery<ServiceListSearchResponse>({
    queryKey: ["focus-service-list", municipalityCode, debouncedQuery],
    enabled: shouldSearch,
    staleTime: 1000 * 60 * 10,
    queryFn: () => fetchServiceList(municipalityCode, debouncedQuery.trim()),
  });
}
